import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAtlasStore } from '../store/atlasStore';
import { PrismElement } from '../types/atlas';
import { atlasClient } from '../api/atlasClient';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import '../styles/PrismElementModal.css';

interface PrismElementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (element: PrismElement) => void;
  mode: 'create' | 'edit';
  dimensionFqn?: string; // Required for create mode
  element?: PrismElement; // Required for edit mode
}

export const PrismElementModal: React.FC<PrismElementModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode,
  dimensionFqn,
  element,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [shouldRemoveExistingImage, setShouldRemoveExistingImage] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    title?: string;
    description?: string;
    image?: string;
  }>({});

  const titleInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setSparkUserInteractionBlockingRefresh = useAtlasStore(
    (state) => state.setSparkUserInteractionBlockingRefresh
  );

  // Get dimensions from store
  const dimensions = useAtlasStore((state) => state.dimensions);
  const fetchDimensions = useAtlasStore((state) => state.fetchDimensions);

  // Helper function to get dimension type name from store
  const getDimensionTypeName = (): string => {
    let dimensionFqnToFind: string;
    if (mode === 'edit' && element) {
      dimensionFqnToFind = element.dimension_fqn;
    } else if (mode === 'create' && dimensionFqn) {
      dimensionFqnToFind = dimensionFqn;
    } else {
      return 'Element'; // fallback
    }
    
    // Find the dimension in the store by FQN
    const dimension = dimensions.find(dim => dim.fqn === dimensionFqnToFind);
    
    // Return the actual dimension name or fallback
    return dimension?.name || 'Element';
  };

  // Ensure dimensions are loaded when modal opens
  useEffect(() => {
    if (isOpen && dimensions.length === 0) {
      fetchDimensions();
    }
  }, [isOpen, dimensions.length, fetchDimensions]);

  // Block background refresh while modal is open
  useEffect(() => {
    if (isOpen) {
      setSparkUserInteractionBlockingRefresh(true);
      return () => setSparkUserInteractionBlockingRefresh(false);
    }
  }, [isOpen, setSparkUserInteractionBlockingRefresh]);

  // Reset form when modal opens/closes or mode changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && element) {
        setTitle(element.name);
        setDescription(element.description);
        setIsActive(element.is_active);
        // Load existing image preview if element has hero image
        if (element.hero_image_id) {
          loadExistingImage(element.hero_image_id);
        } else {
          setImagePreview(null);
        }
      } else {
        setTitle('');
        setDescription('');
        setIsActive(true);
        setImagePreview(null);
      }
      setImageFile(null);
      setShouldRemoveExistingImage(false);
      setError(null);
      setFieldErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, mode, element]);

  // Load existing image for edit mode
  const loadExistingImage = async (imageId: string) => {
    try {
      const url = await atlasClient.getAssetUrl(imageId);
      setImagePreview(url);
    } catch (error) {
      console.error('Failed to load existing image:', error);
      setImagePreview(null);
    }
  };

  // Focus title input when modal opens
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      const timeoutId = setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  const validateForm = () => {
    const errors: typeof fieldErrors = {};
    
    if (!title.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!description.trim()) {
      errors.description = 'Description is required';
    }

    if (imageFile) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(imageFile.type)) {
        errors.image = 'Please select a valid image file (JPEG, PNG, GIF, or WebP)';
      }
      
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (imageFile.size > maxSize) {
        errors.image = 'Image file must be smaller than 10MB';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(file);
    setShouldRemoveExistingImage(false); // Reset removal flag when new image is selected
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    
    // Clear any previous image errors
    setFieldErrors(prev => ({ ...prev, image: undefined }));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    // If we're in edit mode and the element originally had an image, mark it for removal
    if (mode === 'edit' && element?.hero_image_id) {
      setShouldRemoveExistingImage(true);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let updatedElement: PrismElement;

      if (mode === 'create') {
        if (!dimensionFqn) {
          throw new Error('Dimension FQN is required for creating elements');
        }
        
        updatedElement = await atlasClient.createElement({
          dimension_fqn: dimensionFqn,
          name: title.trim(),
          description: description.trim(),
        });
      } else {
        if (!element) {
          throw new Error('Element is required for editing');
        }
        
        updatedElement = await atlasClient.updateElement(element.fqn, {
          name: title.trim(),
          description: description.trim(),
        });
      }

      // Handle image operations
      if (imageFile) {
        // Upload new image
        updatedElement = await atlasClient.uploadElementImage(updatedElement.fqn, imageFile);
      } else if (shouldRemoveExistingImage) {
        // Remove existing image if user explicitly removed it
        updatedElement = await atlasClient.removeElementImage(updatedElement.fqn);
      }

      // Handle activation/deactivation (only in edit mode)
      if (mode === 'edit' && element && element.is_active !== isActive) {
        if (isActive) {
          updatedElement = await atlasClient.activateElement(updatedElement.fqn);
        } else {
          updatedElement = await atlasClient.deactivateElement(updatedElement.fqn);
        }
      }

      onSuccess(updatedElement);
      onClose();
    } catch (err) {
      console.error('Failed to save element:', err);
      setError(err instanceof Error ? err.message : 'Failed to save element');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = isSubmitting || !title.trim() || !description.trim();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!isSubmitDisabled) {
        handleSubmit();
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="prism-modal-overlay">
      <div className="prism-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="prism-modal-header">
          {mode === 'edit' ? (
            <label className="show-all-toggle prism-modal-status-toggle">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={isSubmitting}
              />
              <div className="toggle-slider">
                <div className="toggle-thumb"></div>
              </div>
              <span>{isActive ? 'Active' : 'Inactive'} {getDimensionTypeName()}</span>
            </label>
          ) : (
            <h2>Create New {getDimensionTypeName()}</h2>
          )}
          <button
            className="prism-modal-close"
            onClick={onClose}
            aria-label="Close"
            disabled={isSubmitting}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="prism-modal-body">
          {error && (
            <div className="prism-modal-error" role="alert">
              <ErrorOutlineIcon fontSize="small" />
              {error}
            </div>
          )}

          <div className="prism-form-group">
            <label htmlFor="element-title">
              Title <span className="required">*</span>
            </label>
            <input
              ref={titleInputRef}
              id="element-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter element title..."
              disabled={isSubmitting}
              className={fieldErrors.title ? 'error' : ''}
            />
            {fieldErrors.title && (
              <div className="prism-field-error">
                <ErrorOutlineIcon fontSize="small" />
                {fieldErrors.title}
              </div>
            )}
          </div>

          <div className="prism-form-group">
            <label htmlFor="element-description">
              Description <span className="required">*</span>
            </label>
            <textarea
              id="element-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter element description..."
              disabled={isSubmitting}
              rows={4}
              className={fieldErrors.description ? 'error' : ''}
            />
            {fieldErrors.description && (
              <div className="prism-field-error">
                <ErrorOutlineIcon fontSize="small" />
                {fieldErrors.description}
              </div>
            )}
          </div>

          <div className="prism-form-group">
            <label htmlFor="element-image">Hero Image (optional)</label>
            <div className="prism-image-upload">
              {imagePreview ? (
                <div className="prism-image-preview">
                  <img src={imagePreview} alt="Preview" />
                  <div className="prism-image-actions">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSubmitting}
                      className="prism-image-upload-button"
                    >
                      Upload
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      disabled={isSubmitting}
                      className="prism-image-upload-button"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="prism-image-upload-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                >
                  Upload
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
            </div>
            {fieldErrors.image && (
              <div className="prism-field-error">
                <ErrorOutlineIcon fontSize="small" />
                {fieldErrors.image}
              </div>
            )}
          </div>
        </div>

        <div className="prism-modal-footer">
          <button
            className="prism-btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {isSubmitting ? (
              <>
                <AutorenewIcon className="prism-loading-spinner" fontSize="small" />
                {mode === 'create' ? 'Creating...' : 'Saving...'}
              </>
            ) : (
              mode === 'create' ? 'Create' : 'Save'
            )}
          </button>
          <button
            className="prism-btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}; 
